import { Domaine } from "../theme/tribeTheme";

/* ══════════════════════════════════════════════════════════════════════════
   Plan du back-office : une seule description des 8 entrées du menu et de
   leurs sous-pages. Le menu, l'en-tête (fil d'Ariane) et la page d'accueil de
   chaque entrée la lisent ; aucune autre liste de pages ne doit exister.
   Rangement : revue fonctionnelle du 09/10/2026, section 5, avec les règles
   de la refonte (référentiels et pricing dans Réglages, réputation et
   concours dans Réseau, ventes et facturation dans Argent…).
   ══════════════════════════════════════════════════════════════════════════ */

export interface SousPage {
  titre: string;
  /** Une phrase : ce que l'on fait sur cette page. */
  phrase: string;
  chemin: string;
  /** Réservée au SUPER_ADMIN (même règle que la route : PrivateRoute superAdmin). */
  superAdmin?: boolean;
  /** Page rangée dans une autre entrée : la carte l'indique (« Rangée dans … »). */
  rangeeDans?: Domaine;
}

export interface EntreeMenu {
  cle: Domaine;
  libelle: string;
  chemin: string;
  /** Phrase d'introduction de la page d'accueil de l'entrée. */
  phrase: string;
  sousPages: SousPage[];
}

export const PLAN: EntreeMenu[] = [
  {
    cle: "accueil",
    libelle: "Accueil",
    chemin: "/accueil",
    phrase: "Ce qui attend une décision, l'activité sur le terrain et les chiffres de la semaine.",
    sousPages: [],
  },
  {
    cle: "traiter",
    libelle: "À traiter",
    chemin: "/a-traiter",
    phrase: "Toutes les décisions en attente, de la plus urgente à la moins urgente.",
    sousPages: [
      { titre: "Lieux à valider", chemin: "/a-traiter/lieux",
        phrase: "Valider ou refuser les lieux envoyés par les Spotters, avec photo, analyse et contrôles." },
      { titre: "Réponses aux quêtes", chemin: "/a-traiter/lieux?onglet=reponses",
        phrase: "Les collectes de quêtes encore en attente, toutes quêtes confondues." },
      { titre: "Quêtes à valider", chemin: "/a-traiter/quetes",
        phrase: "Les quêtes et demandes clients à relire avant publication." },
      { titre: "Retraits", chemin: "/argent/retraits", rangeeDans: "argent",
        phrase: "Les demandes de retrait à décider ou à verser." },
    ],
  },
  {
    cle: "quetes",
    libelle: "Quêtes",
    chemin: "/quetes",
    phrase: "Créer, publier et suivre les missions confiées aux Spotters.",
    sousPages: [
      { titre: "Toutes les quêtes", chemin: "/quetes/liste",
        phrase: "Retrouver une quête, ses cycles, son statut et son paiement." },
      { titre: "Créer une quête", chemin: "/quetes/creer",
        phrase: "Définir une mission : contenu, zone, champs, rémunération." },
      { titre: "Demandes clients", chemin: "/quetes/demandes-clients",
        phrase: "Les missions demandées par les clients depuis le portail." },
      { titre: "Quêtes à valider", chemin: "/a-traiter/quetes", rangeeDans: "traiter",
        phrase: "Les quêtes en attente de relecture avant publication." },
    ],
  },
  {
    cle: "lieux",
    libelle: "Lieux",
    chemin: "/lieux",
    phrase: "La base des lieux collectés : à valider, décidés, masqués.",
    sousPages: [
      { titre: "Fichier des lieux", chemin: "/lieux/fichier",
        phrase: "Tous les lieux par statut : approuvés, rejetés, expirés, masqués." },
      { titre: "Lieux à valider", chemin: "/a-traiter/lieux", rangeeDans: "traiter",
        phrase: "La file de validation, lieu par lieu." },
      { titre: "Carte des lieux", chemin: "/analyses/carte", rangeeDans: "analyses",
        phrase: "Explorer les lieux sur une carte et ouvrir leur fiche." },
      { titre: "Types de lieu", chemin: "/reglages/types-de-lieu", rangeeDans: "reglages",
        phrase: "Les catégories et sous-types proposés aux Spotters et aux clients." },
    ],
  },
  {
    cle: "reseau",
    libelle: "Réseau",
    chemin: "/reseau",
    phrase: "Animer les Spotters : activité, réputation, concours et messages.",
    sousPages: [
      { titre: "Spotters", chemin: "/reseau/spotters",
        phrase: "Qui collecte, combien, avec quel taux de validation." },
      { titre: "Restrictions d'accès", chemin: "/reseau/restrictions",
        phrase: "Repérer les comptes à risque, bloquer, débloquer, corriger un solde." },
      { titre: "Réputation", chemin: "/reseau/reputation",
        phrase: "Niveaux, badges et Tips offerts au palier." },
      { titre: "Concours", chemin: "/reseau/concours",
        phrase: "Lancer et suivre les concours entre Spotters." },
      { titre: "Communications", chemin: "/reseau/communications",
        phrase: "Écrire aux Spotters par notification et revoir l'historique." },
      { titre: "Clients invités", chemin: "/reseau/clients-invites",
        phrase: "Inviter un client sur le portail, suivre et annuler les invitations." },
    ],
  },
  {
    cle: "argent",
    libelle: "Argent",
    chemin: "/argent",
    phrase: "Payer les Spotters, fixer la valeur du Tip, vendre et facturer.",
    sousPages: [
      { titre: "Retraits", chemin: "/argent/retraits",
        phrase: "Décider les demandes de retrait et enregistrer les versements Wave." },
      { titre: "Soldes des Spotters", chemin: "/argent/soldes",
        phrase: "Le solde en Tips et en F CFA de chaque Spotter." },
      { titre: "Valeur du Tip et retraits", chemin: "/argent/valeur-du-tip",
        phrase: "Valeur du Tip, minimum, plafond mensuel et second avis." },
      { titre: "Ventes et trésorerie", chemin: "/argent/ventes",
        phrase: "Documents émis, facturé, encaissé et reste dû par quête ; exports comptables." },
      { titre: "Clients facturés", chemin: "/argent/clients",
        phrase: "L'identité fiscale des clients à qui Tribe facture, et leur compte du portail." },
      { titre: "Facturation", chemin: "/argent/facturation",
        phrase: "Entités émettrices par pays et documents émis (devis, contrats, factures)." },
    ],
  },
  {
    cle: "analyses",
    libelle: "Analyses",
    chemin: "/analyses",
    phrase: "Mesurer la couverture et l'activité, exporter les données.",
    sousPages: [
      { titre: "Carte des lieux", chemin: "/analyses/carte",
        phrase: "Les lieux de la zone visible, par statut et par catégorie." },
      { titre: "Tableau des collectes", chemin: "/analyses/collectes",
        phrase: "Compteurs des lieux, densité, évolution sur l'année, catégories." },
      { titre: "Trous de données", chemin: "/analyses/trous",
        phrase: "Ce qui manque dans la base, et la quête qui le comblerait." },
      { titre: "Comparaison des sources", chemin: "/analyses/comparaison-des-sources",
        phrase: "La couverture de Tribe face à Google et OpenStreetMap." },
      { titre: "Exports et rapports", chemin: "/analyses/exports",
        phrase: "Volumes, export ciblé et rapport de couverture." },
    ],
  },
  {
    cle: "reglages",
    libelle: "Réglages",
    chemin: "/reglages",
    phrase: "Référentiels, moteur de prix, contrôles de qualité et équipe.",
    sousPages: [
      { titre: "Types de lieu", chemin: "/reglages/types-de-lieu",
        phrase: "Catégories, sous-types et alias OpenStreetMap." },
      { titre: "Produits", chemin: "/reglages/produits",
        phrase: "Les produits dont les quêtes relèvent les prix." },
      { titre: "Moteur de prix", chemin: "/reglages/moteur-de-prix",
        phrase: "Grilles, coefficients, tarifs de base et simulateur." },
      { titre: "Contrôles et paramètres", chemin: "/reglages/parametres",
        phrase: "Intégrité, analyse photo, fraîcheur, champs des lieux, comptes testeurs." },
      { titre: "Équipe et comptes", chemin: "/reglages/equipe", superAdmin: true,
        phrase: "Les admins du back-office, les comptes Spotters et clients." },
      { titre: "Mon profil", chemin: "/reglages/profil",
        phrase: "Vos informations et votre mot de passe." },
    ],
  },
];

/* Titres des pages hors menu (détail d'une quête…) ou avec paramètre. */
const TITRES_HORS_MENU: { prefixe: string; domaine: Domaine; titre: string }[] = [
  { prefixe: "/reseau/reputation/tips-offerts", domaine: "reseau", titre: "Réputation" },
  { prefixe: "/quetes/", domaine: "quetes", titre: "Détail de la quête" },
];

export function entreeDe(cle: Domaine): EntreeMenu {
  return PLAN.find(e => e.cle === cle)!;
}

/** Entrée du menu dont dépend un chemin (premier segment). */
export function entreeActive(pathname: string): EntreeMenu | undefined {
  return PLAN.find(e => pathname === e.chemin || pathname.startsWith(e.chemin + "/"));
}

/** Domaine et titre de la page affichée, pour l'en-tête. */
export function pageCourante(pathname: string, search = ""): { entree?: EntreeMenu; titre?: string; horsMenu?: boolean } {
  const entree = entreeActive(pathname);
  if (!entree) return {};
  if (pathname === entree.chemin) return { entree };
  const avecRecherche = pathname + search;
  const sp = entree.sousPages.find(p => !p.rangeeDans && p.chemin === avecRecherche)
    ?? entree.sousPages.find(p => !p.rangeeDans && p.chemin === pathname);
  if (sp) return { entree, titre: sp.titre };
  const hors = TITRES_HORS_MENU.find(t => pathname.startsWith(t.prefixe));
  return { entree, titre: hors?.titre, horsMenu: true };
}
